import {Method, MethodNode, ProcedureTestDefinition} from 'procedures/ProcedureDomainTypes';
import TestDefinitionsTableAdapter from 'procedures/tables/TestDefinitionsTableAdapter';

import {
  ProcedureValidationError,
  ProcedureValidationErrorType,
  ProcedureValidationObjectType,
} from '../ProcedureValidationTypes';
import {
  createErrorsForMethodComponentsWithMatchingName,
  extractDuplicateNames,
  extractMethodComponentsNames,
} from '../procedureValidationUtils';

export function validateProcedureTestDefinitions(
  method: Method,
  allMethodNodes: Array<MethodNode>
): Array<ProcedureValidationError> {
  const parentObjects = [{id: method.cid, objectType: ProcedureValidationObjectType.METHOD}];
  const nonEmptyTestDefinitionsInMethod = method.testDefinitions.filter(
    (td): td is ProcedureTestDefinition => !TestDefinitionsTableAdapter.isTestDefinitionEmpty(td)
  );

  const duplicateTestDefinitionInMethodErrors = validateTestDefinitionsWithinMethod(
    nonEmptyTestDefinitionsInMethod,
    parentObjects
  );

  const duplicateTestDefinitionInFlowchartErrors = validateTestDefinitionsWithinFlowchart(
    method,
    nonEmptyTestDefinitionsInMethod,
    allMethodNodes,
    parentObjects
  );

  return duplicateTestDefinitionInMethodErrors.concat(duplicateTestDefinitionInFlowchartErrors);
}

function validateTestDefinitionsWithinMethod(
  testDefinitions: Array<ProcedureTestDefinition>,
  parentObjects: Array<{id: string; objectType: ProcedureValidationObjectType}>
): Array<ProcedureValidationError> {
  const duplicateWithinMethoderrorMsg = 'Test definitions must be unique within a unit operation';

  const testDefinitionNames = extractMethodComponentsNames(testDefinitions);
  const duplicateNames = extractDuplicateNames(testDefinitionNames);

  return duplicateNames.flatMap((name) =>
    createErrorsForMethodComponentsWithMatchingName(
      testDefinitions,
      ProcedureValidationObjectType.TEST_DEFINITION,
      name,
      ProcedureValidationErrorType.DUPLICATE_TEST_DEFINITION,
      duplicateWithinMethoderrorMsg,
      parentObjects
    )
  );
}

function validateTestDefinitionsWithinFlowchart(
  method: Method,
  testDefinitions: Array<ProcedureTestDefinition>,
  allMethodNodes: Array<MethodNode>,
  parentObjects: Array<{id: string; objectType: ProcedureValidationObjectType}>
): Array<ProcedureValidationError> {
  const testDefinitionNamesFromOtherNodes = new Set<string>();
  const duplicateTestDefinitionInFlowchartErrors: Array<ProcedureValidationError> = [];
  const duplicateAcrossMethodsErrorMsg = 'Test definitions must be unique across all unit operations';

  allMethodNodes
    .filter((otherNode) => otherNode.cid !== method.cid)
    .forEach((node) => {
      const nonEmptyTestDefinitions = node.testDefinitions.filter(
        (td): td is ProcedureTestDefinition => !TestDefinitionsTableAdapter.isTestDefinitionEmpty(td)
      );

      nonEmptyTestDefinitions.forEach((td) => {
        testDefinitionNamesFromOtherNodes.add(td.name);
      });
    });

  testDefinitions.forEach((testDef) => {
    if (testDefinitionNamesFromOtherNodes.has(testDef.name)) {
      createErrorsForMethodComponentsWithMatchingName(
        [testDef],
        ProcedureValidationObjectType.TEST_DEFINITION,
        testDef.name,
        ProcedureValidationErrorType.DUPLICATE_TEST_DEFINITION,
        duplicateAcrossMethodsErrorMsg,
        parentObjects
      ).forEach((error) => duplicateTestDefinitionInFlowchartErrors.push(error));
    }
  });

  return duplicateTestDefinitionInFlowchartErrors;
}
